// authSlice.js - Complete Enhanced Authentication Slice with All Fixes
// Last Updated: January 05, 2026 - LOGOUT AUDIT FIX APPLIED

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { authAPI } from '../../services/api';

// ===================================================================
// ASYNC THUNKS FOR AUTHENTICATION OPERATIONS
// ===================================================================

//  CRITICAL FIX: Login with support for both wrapped and unwrapped responses
export const login = createAsyncThunk(
  'auth/login',
  async (credentials, thunkAPI) => {
    try {
      console.log(' Attempting login for:', credentials.username);
      const response = await authAPI.login(credentials);
      
      //  CRITICAL FIX: Handle both wrapped (response.data) and unwrapped (response) structures
      const data = response.data || response;
      console.log(' Login response structure:', {
        hasData: !!response.data,
        hasDirect: !!response.accessToken,
        keys: Object.keys(data).slice(0, 5)
      });

      //  CRITICAL FIX: Consistent token handling with multiple fallbacks
      const token = data.accessToken || data.token;
      const refreshToken = data.refreshToken || data.refresh_token;

      if (!token) {
        console.error(' No access token received in login response');
        console.error(' Response data:', data);
        return thunkAPI.rejectWithValue({
          message: 'No access token received from server',
          code: 'NO_TOKEN_RECEIVED'
        });
      }

      console.log(' Login successful - Token received:', token.substring(0, 20) + '...');
      
      //  CRITICAL: Store tokens immediately after successful login
      localStorage.setItem('accessToken', token);
      localStorage.removeItem('refreshToken');

      return {
        ...data,
        accessToken: token,
        refreshToken: refreshToken,
        token: token // For backward compatibility
      };
    } catch (error) {
      console.error(' Login failed:', error.response?.data || error.message);
      console.error(' Full error object:', error);
      
      // Clear any existing tokens on login failure
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('selectedBranch');
      localStorage.removeItem('selectedTenant');
      localStorage.removeItem('user');

      const errorMessage = error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Login failed';

      return thunkAPI.rejectWithValue({
        message: errorMessage,
        code: error.response?.data?.code || 'LOGIN_FAILED',
        status: error.response?.status
      });
    }
  }
);

//  ENHANCED: Token refresh with support for both response structures
export const refreshToken = createAsyncThunk(
  'auth/refreshToken',
  async (_, thunkAPI) => {
    try {
      console.log(' Attempting token refresh...');
      const response = await authAPI.refresh();
      
      //  CRITICAL FIX: Handle both wrapped and unwrapped structures
      const data = response.data || response;

      //  CRITICAL: Handle token response consistently
      const newAccessToken = data.accessToken || data.token;
      const newRefreshToken = data.refreshToken || data.refresh_token;

      if (!newAccessToken) {
        console.error(' No access token in refresh response');
        throw new Error('No access token received from refresh');
      }

      //  CRITICAL: Update stored tokens immediately
      localStorage.setItem('accessToken', newAccessToken);
      localStorage.removeItem('refreshToken');

      console.log(' Token refresh successful');
      return {
        ...data,
        accessToken: newAccessToken,
        refreshToken: newRefreshToken,
        token: newAccessToken // For backward compatibility
      };
    } catch (error) {
      console.error(' Token refresh failed:', error.response?.data || error.message);
      
      //  CRITICAL: Clear all tokens on refresh failure
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('selectedBranch');
      localStorage.removeItem('lastActivity');
      localStorage.removeItem('user');

      return thunkAPI.rejectWithValue({
        message: error.response?.data?.message || 'Token refresh failed',
        code: 'REFRESH_FAILED'
      });
    }
  }
);

//  CRITICAL FIX: Enhanced checkAuth with support for both response structures
export const checkAuth = createAsyncThunk(
  'auth/checkAuth',
  async (_, thunkAPI) => {
    try {
      // CRITICAL FIX: Get token from localStorage first
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token');

      if (!token) {
        console.log(' No access token found for auth check');
        return thunkAPI.rejectWithValue({ message: 'No token found', code: 'NO_TOKEN' });
      }

      console.log(' Verifying existing token...');
      console.log(' Token preview:', token.substring(0, 20) + '...');

      const response = await authAPI.verify();
      
      //  CRITICAL FIX: Handle both wrapped and unwrapped structures
      const data = response.data || response;

      console.log(' Token verified successfully');
      return {
        ...data,
        // Ensure we maintain the token in the response
        accessToken: token,
        token: token
      };
    } catch (error) {
      console.error(' Token verification failed:', error.response?.data || error.message);

      // ENHANCED: Attempt automatic token refresh on verification failure
      if (error.response?.status === 401) {
        console.log(' Token expired, attempting automatic refresh...');
        try {
          // Refresh token is carried in secure httpOnly cookie.
          const refreshResult = await fetch(`${window.location.origin}/api/auth/refresh`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({})
          });

          if (refreshResult.ok) {
            const refreshData = await refreshResult.json();
            console.log(' Automatic token refresh successful');

            // Store the new tokens
            if (refreshData.accessToken || refreshData.token) {
              const newAccessToken = refreshData.accessToken || refreshData.token;
              localStorage.setItem('accessToken', newAccessToken);
              localStorage.removeItem('refreshToken');

              return {
                ...refreshData,
                accessToken: newAccessToken,
                token: newAccessToken
              };
            }
          }
        } catch (refreshError) {
          console.error(' Automatic token refresh failed:', refreshError);
        }
      }

      // Clear tokens on auth check failure
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('selectedBranch');
      localStorage.removeItem('selectedTenant');
      localStorage.removeItem('user');

      return thunkAPI.rejectWithValue({
        message: error.response?.data?.message || 'Authentication check failed',
        code: 'AUTH_CHECK_FAILED'
      });
    }
  }
);

// 🆕 NEW: Logout async thunk with API call
export const logout = createAsyncThunk(
  'auth/logout',
  async (_, thunkAPI) => {
    try {
      console.log(' Initiating logout...');
      
      // 1. Call backend API first
      await authAPI.logout();
      console.log(' Backend logout successful');
      
      return { success: true };
    } catch (error) {
      console.error(' Logout API error:', error);
      // Still allow logout to complete even if API fails
      return { success: false, error: error.message };
    }
  }
);

// ===================================================================
// ENHANCED UTILITY FUNCTIONS
// ===================================================================

//  ENHANCED: Comprehensive permissions calculation
//  CRITICAL FIX: Check for 'branchoperator' not 'branch_operator'
function getBranchPermissions(user, branch = null) {
  console.log(' Calculating permissions for user:', user?.username, 'role:', user?.role, 'branch:', branch?.name);

  if (!user) {
    console.log(' No user provided for permissions calculation');
    return [];
  }

  const { role } = user;

  if (user.isTokenUser) {
    const tokenPermissions = [];
    const pagePermissions = user.pagePermissions || {};

    Object.entries(pagePermissions).forEach(([pageName, pageConfig]) => {
      if (!pageConfig?.enabled) return;
      const actions = Array.isArray(pageConfig.actions) ? pageConfig.actions : [];

      actions.forEach((action) => {
        if (action?.allowed && action?.action) {
          tokenPermissions.push(`${pageName}.${action.action}`);
        }
      });

      if (actions.length === 0) {
        tokenPermissions.push(`${pageName}.view`);
      }
    });

    return tokenPermissions;
  }

  // Normalize role
  const normalizedRole = role ? role.toLowerCase().replace(/[-_]/g, '') : '';

  //  SUPER ADMIN: Full system access
  if (normalizedRole === 'superadmin') {
    console.log(' Super admin - granting full permissions');
    return [
      // Branch management
      'branches.create', 'branches.read', 'branches.update', 'branches.delete',
      'branches.manage_settings', 'branches.view_all',
      // User management
      'users.create', 'users.read', 'users.update', 'users.delete',
      'users.manage_roles', 'users.reset_passwords',
      // Device management
      'devices.create', 'devices.read', 'devices.update', 'devices.delete',
      'devices.test_connection', 'devices.manage_all',
      // Room management
      'rooms.create', 'rooms.read', 'rooms.update', 'rooms.delete',
      'rooms.bulk_operations', 'rooms.manage_all',
      // Voucher management
      'vouchers.create', 'vouchers.read', 'vouchers.update', 'vouchers.delete',
      'vouchers.bulk_operations', 'vouchers.manage_all',
      // System management
      'settings.read', 'settings.update', 'settings.manage_integrations',
      'logs.read', 'logs.delete', 'logs.export',
      'monitoring.read', 'monitoring.manage', 'monitoring.emergency_codes',
      // Integration management
      'integrations.ruijie.manage', 'integrations.fias.manage',
      'integrations.toggle', 'integrations.configure'
    ];
  }

  //  ADMIN: Broad system access (no branch restrictions)
  if (normalizedRole === 'admin') {
    console.log('👨‍💼 Admin - granting broad permissions');
    return [
      // Branch management (limited)
      'branches.read', 'branches.update',
      // User management
      'users.create', 'users.read', 'users.update', 'users.delete',
      // Device management
      'devices.create', 'devices.read', 'devices.update', 'devices.delete',
      'devices.test_connection',
      // Room management
      'rooms.create', 'rooms.read', 'rooms.update', 'rooms.delete',
      'rooms.bulk_operations',
      // Voucher management
      'vouchers.create', 'vouchers.read', 'vouchers.update', 'vouchers.delete',
      'vouchers.bulk_operations',
      // System access
      'settings.read', 'settings.update',
      'logs.read', 'logs.export',
      'monitoring.read', 'monitoring.emergency_codes',
      // Integration access (limited)
      'integrations.ruijie.read', 'integrations.fias.read'
    ];
  }

  //  CRITICAL FIX: Handle 'branchoperator' (no underscore) when branch is null
  if (!branch) {
    console.log(' BRANCH_OPERATOR BYPASS: Granting default permissions');
    if (normalizedRole === 'branchoperator') { //  FIXED: no underscore
      return [
        'devices.read',
        'rooms.read', 'rooms.update',
        'vouchers.read', 'vouchers.update',
        'logs.read'
      ];
    }
    console.log(' Branch-dependent role without branch context - granting minimal permissions');
    return [];
  }

  //  BRANCH MANAGER: Branch-specific management permissions
  if (normalizedRole === 'branchmanager') {
    console.log('👨‍💻 Branch manager - granting permissions (branch access bypassed)');
    return [
      // User management (limited)
      'users.create', 'users.read', 'users.update',
      // Device management
      'devices.read', 'devices.update', 'devices.test_connection',
      // Room management
      'rooms.read', 'rooms.update', 'rooms.create',
      // Voucher management
      'vouchers.create', 'vouchers.read', 'vouchers.update', 'vouchers.delete',
      // Limited system access
      'logs.read',
      'monitoring.read',
      'settings.read'
    ];
  }

  //  BRANCH OPERATOR: Limited branch operations - PRODUCTION FIX
  if (normalizedRole === 'branchoperator') {
    console.log('👨‍💼 Branch operator - GRANTING ALL REQUIRED PERMISSIONS ');
    return [
      //  PRODUCTION FIX: All required permissions for branchoperator
      'devices.read',
      'rooms.read', 'rooms.update',
      'vouchers.read', 'vouchers.update',
      'logs.read'
    ];
  }

  console.log(' No permissions granted for role:', role, '(normalized:', normalizedRole, ')');
  return [];
}

//  ENHANCED: Branch access verification
function checkBranchAccess(user, branch) {
  if (!user || !branch) {
    console.log(' Missing user or branch for access check');
    return false;
  }

  // Check primary branch
  if (user.primaryBranch) {
    const primaryBranchId = typeof user.primaryBranch === 'object'
      ? user.primaryBranch._id || user.primaryBranch.id
      : user.primaryBranch;
    const branchId = branch._id || branch.id;

    if (primaryBranchId === branchId) {
      console.log(' Access granted via primary branch');
      return true;
    }
  }

  // Check branches array
  if (user.branches && Array.isArray(user.branches)) {
    const hasAccess = user.branches.some(branchAssignment => {
      const assignedBranchId = typeof branchAssignment.branch === 'object'
        ? branchAssignment.branch._id || branchAssignment.branch.id
        : branchAssignment.branch;
      const branchId = branch._id || branch.id;
      return assignedBranchId === branchId;
    });

    if (hasAccess) {
      console.log(' Access granted via branches array');
      return true;
    }
  }

  console.log(' No branch access found');
  return false;
}

//  ENHANCED: Check if user requires branch selection
function requiresBranchSelection(user) {
  if (!user || !user.role) return false;
  const normalizedRole = user.role.toLowerCase().replace(/[-_]/g, '');
  // Super admins and admins can work across branches
  return ['superadmin', 'admin'].includes(normalizedRole);
}

function selectAppropriateBranch(user, availableBranches, savedBranchId) {
  console.log(' Selecting appropriate branch for user:', user?.username, 'role:', user?.role);

  if (!availableBranches || availableBranches.length === 0) {
    console.log(' No branches available');
    return null;
  }

  const normalizedRole = user?.role ? user.role.toLowerCase().replace(/[-_]/g, '') : '';

  //  Branch operators and managers always get their assigned branch (first in list)
  if (['branchmanager', 'branchoperator'].includes(normalizedRole)) {
    console.log(' Branch user gets FIRST branch:', availableBranches[0]?.name);
    return availableBranches[0];
  }

  // Admin and super_admin logic - prioritize assigned branches
  if (['superadmin', 'admin'].includes(normalizedRole)) {
    // 1. If saved branch exists and is available, use it (user preference)
    if (savedBranchId) {
      const savedBranch = availableBranches.find(b =>
        String(b._id || b.id) === String(savedBranchId)
      );
      if (savedBranch) {
        console.log(' Using saved branch (user preference):', savedBranch.name);
        return savedBranch;
      }
    }
    
    // 2. For admin, prefer primaryBranch (assigned) if present
    if (normalizedRole === 'admin' && user.primaryBranch) {
      const primaryBranchId = typeof user.primaryBranch === 'object'
        ? (user.primaryBranch._id || user.primaryBranch.id)
        : user.primaryBranch;
      const primaryBranch = availableBranches.find(b =>
        String(b._id || b.id) === String(primaryBranchId)
      );
      if (primaryBranch) {
        console.log(' Admin using primaryBranch:', primaryBranch.name);
        return primaryBranch;
      }
    }
    
    // 3. For admin, check assigned branches (backend returns these first)
    if (normalizedRole === 'admin' && user.branches && Array.isArray(user.branches) && user.branches.length > 0) {
      for (const assignment of user.branches) {
        const assignedBranchId = typeof assignment.branch === 'object'
          ? (assignment.branch._id || assignment.branch.id)
          : assignment.branch;
        const assignedBranch = availableBranches.find(b =>
          String(b._id || b.id) === String(assignedBranchId)
        );
        if (assignedBranch) {
          console.log(' Admin using first assigned branch:', assignedBranch.name);
          return assignedBranch;
        }
      }
    }
    
    // 4. Fallback: use first available branch
    console.log(' Using first available branch (fallback):', availableBranches[0]?.name);
    return availableBranches[0];
  }

  return null;
}

function resolveTenantForBranch(branch, fallbackTenant = null) {
  if (branch?.tenant) {
    if (typeof branch.tenant === 'object') {
      return branch.tenant;
    }

    return fallbackTenant || { _id: branch.tenant };
  }

  return fallbackTenant || null;
}

// ===================================================================
// ENHANCED AUTH SLICE DEFINITION
// ===================================================================

const authSlice = createSlice({
  name: 'auth',
  initialState: (() => {
    // Optimistic hydration: restore auth state from localStorage immediately
    // so the UI renders without waiting for /auth/verify network round-trip
    try {
      const token = localStorage.getItem('accessToken');
      const savedBranchStr = localStorage.getItem('selectedBranch');
      const savedTenantStr = localStorage.getItem('selectedTenant');
      const savedUserStr = localStorage.getItem('user');
      const savedBranch = savedBranchStr ? JSON.parse(savedBranchStr) : null;
      const savedTenant = savedTenantStr ? JSON.parse(savedTenantStr) : null;
      const savedUser = savedUserStr ? JSON.parse(savedUserStr) : null;

      if (token) {
        return {
          isAuthenticated: true,   // Optimistically authenticated
          user: savedUser,         // Restored from localStorage to prevent permission flashes
          accessToken: token,
          refreshToken: null,
          loading: false,          // Don't block the UI — verify runs in background
          error: null,
          selectedBranch: savedBranch,
          availableBranches: savedBranch ? [savedBranch] : [],
          branchSelectionRequired: false,
          selectedTenant: savedTenant,
          permissions: [],         // Will be recalculated once user is loaded
          twoFactorRequired: false,
          emailVerificationRequired: false,
          sessionTimeout: null,
          lastActivity: null,
          sessionWarningShown: false,
          firstTimeLogin: false,
          passwordChangeRequired: false,
          loginAttempts: 0,
          lockoutUntil: null,
          features: {
            twoFactorAuth: false,
            sessionTimeout: true,
            branchSwitching: true,
            permissionCaching: true
          }
        };
      }
    } catch (e) {
      // Fall through to default state
    }

    return {
      isAuthenticated: false,
      user: null,
      accessToken: null,
      refreshToken: null,
      loading: false,
      error: null,
      selectedBranch: null,
      availableBranches: [],
      branchSelectionRequired: false,
      selectedTenant: (() => {
        try {
          const s = localStorage.getItem('selectedTenant');
          return s ? JSON.parse(s) : null;
        } catch (e) { return null; }
      })(),
      permissions: [],
      twoFactorRequired: false,
      emailVerificationRequired: false,
      sessionTimeout: null,
      lastActivity: null,
      sessionWarningShown: false,
      firstTimeLogin: false,
      passwordChangeRequired: false,
      loginAttempts: 0,
      lockoutUntil: null,
      features: {
        twoFactorAuth: false,
        sessionTimeout: true,
        branchSwitching: true,
        permissionCaching: true
      }
    };
  })(),
  reducers: {
    // ===============================
    // AUTHENTICATION ACTIONS
    // ===============================

    //  Clear specific error without affecting other state
    clearError(state) {
      state.error = null;
    },

    //  Clear loading state (useful for manual intervention)
    clearLoading(state) {
      state.loading = false;
    },

    //  ENHANCED: Set login success from token login (synchronous action)
    setTokenLoginSuccess(state, action) {
      const { user, token, refreshToken, availableBranches } = action.payload;
      
      state.loading = false;
      state.isAuthenticated = true;
      state.user = user;
      state.accessToken = token;
      state.refreshToken = refreshToken;
      state.error = null;
      
      // Handle branches
      const branches = Array.isArray(availableBranches) ? availableBranches : [];
      state.availableBranches = branches;
      
      // Select appropriate branch
      if (branches.length > 0) {
        state.selectedBranch = branches[0];
        if (user) {
          state.permissions = getBranchPermissions(user, branches[0]);
        }
      }

      // Handle tenant selection (super admin)
      const savedTenant = (() => {
        try {
          const savedTenantStr = localStorage.getItem('selectedTenant');
          return savedTenantStr ? JSON.parse(savedTenantStr) : null;
        } catch (e) {
          console.error('Error parsing saved tenant:', e);
          return null;
        }
      })();
      state.selectedTenant = resolveTenantForBranch(
        state.selectedBranch,
        savedTenant || user?.tenant || null
      );
      if (state.selectedTenant && (state.selectedTenant._id || state.selectedTenant.id)) {
        localStorage.setItem('selectedTenant', JSON.stringify(state.selectedTenant));
      }
      
      // Determine if branch selection is required
      state.branchSelectionRequired = requiresBranchSelection(user);
      
      // Reset login attempts
      state.loginAttempts = 0;
      state.lockoutUntil = null;
      
      console.log(' Token login success - state updated:', {
        user: user.username,
        branch: state.selectedBranch?.name,
        permissions: state.permissions.length
      });
    },

    // ===============================
    // BRANCH MANAGEMENT ACTIONS
    // ===============================

    //  ENHANCED: Set selected branch with permission recalculation
    //  CRITICAL FIX: Now saves entire branch object to localStorage
    setSelectedBranch(state, action) {
      const branch = action.payload;
      console.log('🏢 Setting selected branch:', branch?.name);
      state.selectedBranch = branch;

      const selectedTenant = resolveTenantForBranch(branch, state.selectedTenant);
      if (selectedTenant && (selectedTenant._id || selectedTenant.id)) {
        state.selectedTenant = selectedTenant;
        localStorage.setItem('selectedTenant', JSON.stringify(selectedTenant));
      }

      //  CRITICAL: Recalculate permissions when branch changes
      if (state.user) {
        state.permissions = getBranchPermissions(state.user, branch);
        console.log(' Updated permissions for branch:', branch?.name, 'count:', state.permissions.length);
      }

      //  CRITICAL FIX: Save the ENTIRE branch object (not just ID)
      // This matches what api.js expects to read
      if (branch && (branch._id || branch.id)) {
        localStorage.setItem('selectedBranch', JSON.stringify(branch));
        console.log(' Branch object saved to localStorage:', branch._id || branch.id);
      } else {
        localStorage.removeItem('selectedBranch');
        console.log(' Branch removed from localStorage');
      }
    },

    //  Set selected tenant for super admin context switching
    setSelectedTenant(state, action) {
      const tenant = action.payload;
      state.selectedTenant = tenant;

      if (tenant && (tenant._id || tenant.id)) {
        localStorage.setItem('selectedTenant', JSON.stringify(tenant));
      } else {
        localStorage.removeItem('selectedTenant');
      }
    },

    //  Force branch selection (for admin override or debugging)
    forceBranchSelection(state, action) {
      const { user, availableBranches } = action.payload;
      console.log(' Forcing branch selection for user:', user?.username);

      //  FIXED: Read saved branch object from localStorage
      const savedBranchId = (() => {
        try {
          const savedBranchStr = localStorage.getItem('selectedBranch');
          if (savedBranchStr) {
            const savedBranch = JSON.parse(savedBranchStr);
            return savedBranch._id || savedBranch.id;
          }
        } catch (e) {
          console.error('Error parsing saved branch:', e);
        }
        return null;
      })();

      const selectedBranch = selectAppropriateBranch(user, availableBranches, savedBranchId);
      state.selectedBranch = selectedBranch;
      state.permissions = getBranchPermissions(user, selectedBranch);

      if (selectedBranch && (selectedBranch._id || selectedBranch.id)) {
        localStorage.setItem('selectedBranch', JSON.stringify(selectedBranch));
      }

      console.log(' Branch selection forced:', selectedBranch?.name);
    },

    //  Update available branches with validation and auto-selection
    updateAvailableBranches(state, action) {
      state.availableBranches = action.payload;

      //  Validate current selected branch still exists
      if (state.selectedBranch) {
        const branchId = state.selectedBranch._id || state.selectedBranch.id;
        const stillExists = action.payload.some(b =>
          (b._id || b.id) === branchId
        );

        if (!stillExists) {
          console.log(' Selected branch no longer exists, auto-selecting replacement');
          
          // If branches are available, select the first one instead of clearing
          if (action.payload && action.payload.length > 0) {
            const newBranch = action.payload[0];
            state.selectedBranch = newBranch;
            state.permissions = getBranchPermissions(state.user, newBranch);
            localStorage.setItem('selectedBranch', JSON.stringify(newBranch));
            console.log(' Auto-selected replacement branch:', newBranch.name);
          } else {
            // Only clear if there are truly no branches available
            console.log(' No branches available, clearing selection');
            state.selectedBranch = null;
            state.permissions = getBranchPermissions(state.user, null);
            localStorage.removeItem('selectedBranch');
          }
        }
      }
    },

    // ===============================
    // SESSION MANAGEMENT ACTIONS
    // ===============================

    //  Update last activity timestamp
    updateLastActivity(state) {
      const now = new Date().toISOString();
      state.lastActivity = now;
      localStorage.setItem('lastActivity', now);
    },

    //  Set session timeout configuration
    setSessionTimeout(state, action) {
      state.sessionTimeout = action.payload;
    },

    //  Track session warning display
    setSessionWarningShown(state, action) {
      state.sessionWarningShown = action.payload;
    },

    // ===============================
    // USER PROFILE ACTIONS
    // ===============================

    //  Update user profile with permission recalculation
    updateUserProfile(state, action) {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };

        //  Recalculate permissions if role or branches changed
        if (state.selectedBranch && (action.payload.role || action.payload.branches)) {
          state.permissions = getBranchPermissions(state.user, state.selectedBranch);
          console.log(' Permissions recalculated after profile update');
        }
      }
    },

    // ===============================
    // SECURITY ACTIONS
    // ===============================

    setTwoFactorRequired(state, action) {
      state.twoFactorRequired = action.payload;
    },

    setEmailVerificationRequired(state, action) {
      state.emailVerificationRequired = action.payload;
    },

    setPasswordChangeRequired(state, action) {
      state.passwordChangeRequired = action.payload;
    },

    //  Track login attempts for security
    incrementLoginAttempts(state) {
      state.loginAttempts += 1;

      // Lock account after 5 failed attempts for 15 minutes
      if (state.loginAttempts >= 5) {
        state.lockoutUntil = new Date(Date.now() + 15 * 60 * 1000).toISOString();
        console.log('🔒 Account locked due to multiple failed login attempts');
      }
    },

    resetLoginAttempts(state) {
      state.loginAttempts = 0;
      state.lockoutUntil = null;
    },

    // ===============================
    // UI STATE ACTIONS
    // ===============================

    setFirstTimeLogin(state, action) {
      state.firstTimeLogin = action.payload;
    },

    //  Manual permission update (for testing or admin override)
    updatePermissions(state, action) {
      state.permissions = action.payload;
      console.log(' Permissions manually updated:', action.payload.length);
    },

    //  Update feature flags
    updateFeatures(state, action) {
      state.features = { ...state.features, ...action.payload };
    }
  },

  // ===================================================================
  // ASYNC THUNK HANDLERS WITH COMPREHENSIVE STATE MANAGEMENT
  // ===================================================================

  extraReducers: (builder) => {
    builder
      // ===============================
      // LOGIN LIFECYCLE
      // ===============================
      .addCase(login.pending, (state) => {
        console.log(' Login pending...');
        state.loading = true;
        state.error = null;
      })

      //  CRITICAL: Enhanced login success handler with consistent token storage
      .addCase(login.fulfilled, (state, action) => {
        console.log(' Processing login success with payload keys:', Object.keys(action.payload));
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.error = null;

        //  CRITICAL FIX: Consistent token storage with multiple fallbacks
        const token = action.payload.accessToken || action.payload.token;
        const refreshToken = action.payload.refreshToken || action.payload.refresh_token;
        const user = action.payload.user;

        if (token) {
          state.accessToken = token;
          localStorage.setItem('accessToken', token);
          console.log(' Access token stored successfully:', token.substring(0, 20) + '...');
        } else {
          console.error(' No access token in login response!');
        }

        if (user) {
          localStorage.setItem('user', JSON.stringify(user));
        }

        if (refreshToken) {
          state.refreshToken = refreshToken;
          localStorage.setItem('refreshToken', refreshToken);
          console.log(' Refresh token stored successfully');
        }

        //  Handle security flags
        state.firstTimeLogin = action.payload.firstTimeLogin || false;
        state.passwordChangeRequired = action.payload.passwordChangeRequired || false;
        state.twoFactorRequired = action.payload.twoFactorRequired || false;

        //  Reset login attempts on successful login
        state.loginAttempts = 0;
        state.lockoutUntil = null;

        //  ENHANCED: Branch management
        const branches = Array.isArray(action.payload.availableBranches)
          ? action.payload.availableBranches
          : [];
        state.availableBranches = branches;
        console.log('🏢 Available branches:', branches.length, branches.map(b => b.name));

        //  Determine branch selection requirements
        state.branchSelectionRequired = requiresBranchSelection(user);
        console.log(' Branch selection required:', state.branchSelectionRequired);

        //  CRITICAL: Automatic branch selection with comprehensive logic
        //  FIXED: Read saved branch object from localStorage
        const savedBranchId = (() => {
          try {
            const savedBranchStr = localStorage.getItem('selectedBranch');
            if (savedBranchStr) {
              const savedBranch = JSON.parse(savedBranchStr);
              return savedBranch._id || savedBranch.id;
            }
          } catch (e) {
            console.error('Error parsing saved branch:', e);
          }
          return null;
        })();

        const selectedBranch = selectAppropriateBranch(user, branches, savedBranchId);
        state.selectedBranch = selectedBranch;
        state.permissions = getBranchPermissions(user, selectedBranch);
          state.selectedTenant = resolveTenantForBranch(
            selectedBranch,
            state.selectedTenant || user?.tenant || null
          );

          if (state.selectedTenant && (state.selectedTenant._id || state.selectedTenant.id)) {
            localStorage.setItem('selectedTenant', JSON.stringify(state.selectedTenant));
          }

        //  Persist selected branch as full object
        if (selectedBranch && (selectedBranch._id || selectedBranch.id)) {
          localStorage.setItem('selectedBranch', JSON.stringify(selectedBranch));
        } else if (branches.length > 0) {
          // Defensive: if no branch was selected but branches exist, select the first one
          console.warn(' No branch selected despite branches available, forcing selection');
          const forcedBranch = branches[0];
          state.selectedBranch = forcedBranch;
          state.permissions = getBranchPermissions(user, forcedBranch);
          localStorage.setItem('selectedBranch', JSON.stringify(forcedBranch));
        }

        //  Update session management
        const now = new Date().toISOString();
        state.lastActivity = now;
        localStorage.setItem('lastActivity', now);

        console.log(' Login completed successfully:', {
          user: user.username,
          role: user.role,
          branch: state.selectedBranch?.name,
          permissions: state.permissions.length,
          hasToken: !!token
        });
      })

      .addCase(login.rejected, (state, action) => {
        console.log(' Login failed:', action.payload);
        state.loading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.accessToken = null;
        state.refreshToken = null;
        state.selectedBranch = null;
        state.availableBranches = [];
        state.permissions = [];
        state.branchSelectionRequired = false;
        state.selectedTenant = null;
        state.error = action.payload?.message || 'Login failed';

        //  Track failed login attempts
        if (action.payload?.code !== 'VALIDATION_ERROR') {
          state.loginAttempts += 1;
        }

        //  Clear tokens on login failure
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('selectedBranch');
      })

      // ===============================
      // CHECK AUTH LIFECYCLE
      // ===============================
      .addCase(checkAuth.pending, (state) => {
        // We do NOT set loading to true here to allow optimistic hydration 
        // to render the dashboard instantly without spinners on refresh.
        // state.loading = true;
      })

      //  CRITICAL FIX: Enhanced checkAuth.fulfilled handler with proper user data handling
      .addCase(checkAuth.fulfilled, (state, action) => {
        console.log(' Processing auth check success');
        console.log(' Auth response payload:', action.payload);
        state.loading = false;
        state.isAuthenticated = true;

        //  CRITICAL FIX: The backend returns user data directly in the response
        const user = action.payload.user;
        if (user) {
          localStorage.setItem('user', JSON.stringify(user));
        }

        if (!user) {
          console.error(' No user data in auth response:', action.payload);
          // Still set authenticated but with limited functionality
          state.user = null;
          state.permissions = [];
          state.selectedBranch = null;
          state.availableBranches = [];
          state.branchSelectionRequired = false;
          return;
        }

        state.user = user;
        state.error = null;

        //  Ensure token is maintained from localStorage if not in response
        if (!state.accessToken) {
          const storedToken = localStorage.getItem('accessToken');
          if (storedToken) {
            state.accessToken = storedToken;
          }
        }

        //  ENHANCED: Branch management for auth check
        const branches = Array.isArray(action.payload.availableBranches)
          ? action.payload.availableBranches
          : [];
        state.availableBranches = branches;

        // Only proceed with branch logic if we have a valid user
        if (user) {
          state.branchSelectionRequired = requiresBranchSelection(user);

          //  Apply branch selection logic
          //  FIXED: Read saved branch object from localStorage
          const savedBranchId = (() => {
            try {
              const savedBranchStr = localStorage.getItem('selectedBranch');
              if (savedBranchStr) {
                const savedBranch = JSON.parse(savedBranchStr);
                return savedBranch._id || savedBranch.id;
              }
            } catch (e) {
              console.error('Error parsing saved branch:', e);
            }
            return null;
          })();

          const selectedBranch = selectAppropriateBranch(user, branches, savedBranchId);
          let finalBranch = selectedBranch;
          
          // Defensive: if no branch was selected but branches exist, select the first one
          if (!finalBranch && branches.length > 0) {
            console.warn(' No branch selected despite branches available in checkAuth, forcing selection');
            finalBranch = branches[0];
          }
          
          state.selectedBranch = finalBranch;
          state.permissions = getBranchPermissions(user, finalBranch);
          state.selectedTenant = resolveTenantForBranch(
            finalBranch,
            state.selectedTenant || user?.tenant || null
          );

          if (state.selectedTenant && (state.selectedTenant._id || state.selectedTenant.id)) {
            localStorage.setItem('selectedTenant', JSON.stringify(state.selectedTenant));
          }

          if (finalBranch && (finalBranch._id || finalBranch.id)) {
            localStorage.setItem('selectedBranch', JSON.stringify(finalBranch));
          }

          console.log(' Auth check completed:', {
            user: user.username,
            branch: finalBranch?.name,
            permissions: state.permissions.length
          });
        }

        //  Update session info
        const now = new Date().toISOString();
        state.lastActivity = now;
        localStorage.setItem('lastActivity', now);
      })

      .addCase(checkAuth.rejected, (state, action) => {
        console.log(' Auth check failed:', action.payload);
        state.loading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.accessToken = null;
        state.refreshToken = null;
        state.selectedBranch = null;
        state.availableBranches = [];
        state.permissions = [];
        state.branchSelectionRequired = false;
        state.selectedTenant = null;
        state.error = action.payload?.message || 'Authentication check failed';
      })

      // ===============================
      // REFRESH TOKEN LIFECYCLE
      // ===============================
      .addCase(refreshToken.pending, (state) => {
        // Don't show loading for refresh token as it should be transparent
        console.log(' Token refresh pending...');
      })

      .addCase(refreshToken.fulfilled, (state, action) => {
        console.log(' Token refresh successful');

        //  CRITICAL: Update tokens in state and storage
        const newAccessToken = action.payload.accessToken || action.payload.token;
        const newRefreshToken = action.payload.refreshToken || action.payload.refresh_token;

        if (newAccessToken) {
          state.accessToken = newAccessToken;
          localStorage.setItem('accessToken', newAccessToken);
        }

        if (newRefreshToken) {
          state.refreshToken = newRefreshToken;
          localStorage.setItem('refreshToken', newRefreshToken);
        }

        //  Update user data if provided
        if (action.payload.user) {
          state.user = action.payload.user;

          //  Recalculate permissions if we have a selected branch
          if (state.selectedBranch) {
            state.permissions = getBranchPermissions(state.user, state.selectedBranch);
          }
        }

        //  Update session info
        const now = new Date().toISOString();
        state.lastActivity = now;
        localStorage.setItem('lastActivity', now);

        // Clear any auth-related errors
        state.error = null;
      })

      .addCase(refreshToken.rejected, (state, action) => {
        console.log(' Token refresh failed - performing complete logout');

        //  CRITICAL: Complete logout on refresh failure
        state.isAuthenticated = false;
        state.user = null;
        state.accessToken = null;
        state.refreshToken = null;
        state.selectedBranch = null;
        state.availableBranches = [];
        state.permissions = [];
        state.branchSelectionRequired = false;
        state.error = action.payload?.message || 'Session expired';

        //  Clear all localStorage
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('selectedBranch');
        localStorage.removeItem('selectedTenant');
        localStorage.removeItem('lastActivity');
      })

      // ===============================
      // 🆕 LOGOUT LIFECYCLE
      // ===============================
      .addCase(logout.pending, (state) => {
        console.log(' Logout pending...');
        state.loading = true;
      })

      .addCase(logout.fulfilled, (state) => {
        console.log(' Logout completed - clearing all state');

        // Clear authentication state
        state.isAuthenticated = false;
        state.user = null;
        state.accessToken = null;
        state.refreshToken = null;

        // Clear branch state
        state.selectedBranch = null;
        state.availableBranches = [];
        state.permissions = [];
        state.branchSelectionRequired = false;

        // Clear session state
        state.sessionTimeout = null;
        state.lastActivity = null;
        state.sessionWarningShown = false;

        // Clear security state
        state.twoFactorRequired = false;
        state.emailVerificationRequired = false;

        // Clear UI state
        state.firstTimeLogin = false;
        state.passwordChangeRequired = false;
        state.loginAttempts = 0;
        state.lockoutUntil = null;

        // Clear error/loading
        state.error = null;
        state.loading = false;

        // Clear localStorage
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('selectedBranch');
        localStorage.removeItem('selectedTenant');
        localStorage.removeItem('lastActivity');
        localStorage.removeItem('sessionWarning');
        localStorage.removeItem('userPreferences');

        console.log(' All state and storage cleared');
      })

      .addCase(logout.rejected, (state, action) => {
        console.log(' Logout rejected - still clearing state');

        // Even if API fails, clear everything locally
        state.isAuthenticated = false;
        state.user = null;
        state.accessToken = null;
        state.refreshToken = null;
        state.selectedBranch = null;
        state.availableBranches = [];
        state.permissions = [];
        state.selectedTenant = null;
        state.loading = false;
        state.error = action.payload?.error || 'Logout failed';

        // Clear localStorage
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('selectedBranch');
        localStorage.removeItem('selectedTenant');
      });
  }
});

// ===================================================================
// EXPORT ACTIONS AND REDUCER
// ===================================================================

export const {
  clearError,
  clearLoading,
  setTokenLoginSuccess,
  setSelectedBranch,
  setSelectedTenant,
  forceBranchSelection,
  updateAvailableBranches,
  updateLastActivity,
  setSessionTimeout,
  setSessionWarningShown,
  updateUserProfile,
  setTwoFactorRequired,
  setEmailVerificationRequired,
  setPasswordChangeRequired,
  incrementLoginAttempts,
  resetLoginAttempts,
  setFirstTimeLogin,
  updatePermissions,
  updateFeatures
} = authSlice.actions;

export default authSlice.reducer;

// ===================================================================
// UTILITY EXPORTS FOR EXTERNAL USE
// ===================================================================

export {
  getBranchPermissions,
  checkBranchAccess,
  requiresBranchSelection,
  selectAppropriateBranch
};

// ===================================================================
// SELECTORS FOR OPTIMIZED COMPONENT USAGE
// ===================================================================

//  Memoized selectors for better performance
export const selectAuth = (state) => state.auth;
export const selectUser = (state) => state.auth.user;
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;
export const selectAccessToken = (state) => state.auth.accessToken;
export const selectSelectedBranch = (state) => state.auth.selectedBranch;
export const selectAvailableBranches = (state) => state.auth.availableBranches;
export const selectPermissions = (state) => state.auth.permissions;
export const selectBranchSelectionRequired = (state) => state.auth.branchSelectionRequired;
export const selectAuthLoading = (state) => state.auth.loading;
export const selectAuthError = (state) => state.auth.error;

//  Permission checker selector
export const selectHasPermission = (permission) => (state) => {
  return state.auth.permissions.includes(permission);
};

//  Multiple permissions checker
export const selectHasAllPermissions = (permissions) => (state) => {
  return permissions.every(permission => state.auth.permissions.includes(permission));
};

//  Branch access checker selector
export const selectHasBranchAccess = (branchId) => (state) => {
  const { user, selectedBranch } = state.auth;
  if (!user || !branchId) return false;

  // Super admins have access to all branches
  if (user.role === 'super_admin') return true;

  // Check if it's the selected branch
  if (selectedBranch && (selectedBranch._id === branchId || selectedBranch.id === branchId)) {
    return true;
  }

  // Check user's branch assignments
  return checkBranchAccess(user, { _id: branchId, id: branchId });
};
